import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { FadingStreamText } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/FadingStreamText'
import { ResponseSegmentCitations } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/ResponseSegmentCitations'
import { SubjectLinkedAgenticMarkdown } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/SubjectLinkedAgenticMarkdown'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { adjacentDuplicateValueCitationKeys } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/datapointValueCitations'
import { getDocChatResponseBodyText } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatText'
import type {
  ResponseSegment,
  TimeSeriesComputedSeries,
  TimeSeriesSeriesSummary,
  TimeSeriesTrendRef,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { buildCitationDocumentLookup } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/resolveCitationDocument'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import type {
  DocChatResponseEvent,
  DocChatResponsePartialEvent,
} from 'features/knowledgeDocumentViewer/types'
import React, { useId, useMemo, useState } from 'react'

import {
  ResponseDetailsBody,
  ThoughtsChevron,
  ThoughtsSummary,
  ThoughtsToggle,
} from './docChatStyles'
import type { PreviewDocumentHandler } from './previewDocumentHandler'

function ResponseBodyText({
  isStreaming,
  text,
}: {
  isStreaming: boolean
  text: string
}): React.ReactElement | null {
  if (!text) return null
  if (isStreaming) {
    return <FadingStreamText text={text} />
  }
  return <SubjectLinkedAgenticMarkdown>{text}</SubjectLinkedAgenticMarkdown>
}

/** A `details` segment: a collapsed row that opens to secondary Markdown. */
function ResponseDetailsSegment({
  text,
  title,
  isStreaming,
}: {
  text: string
  title?: string | null
  isStreaming: boolean
}): React.ReactElement {
  const { t } = useLingui()
  const [isOpen, setIsOpen] = useState(false)
  const contentId = useId()

  return (
    <Box padding='xs' minWidth='none'>
      <ThoughtsToggle
        type='button'
        aria-controls={contentId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}>
        <ThoughtsSummary>
          {title?.trim() || t`Check how this was calculated`}
        </ThoughtsSummary>
        <ThoughtsChevron $open={isOpen} aria-hidden='true' size={14} />
      </ThoughtsToggle>
      {isOpen ? (
        <ResponseDetailsBody id={contentId}>
          <ResponseBodyText isStreaming={isStreaming} text={text} />
        </ResponseDetailsBody>
      ) : null}
    </Box>
  )
}

function CitedResponseSegment({
  text,
  citations,
  citationLookup,
  timeSeriesSummaries,
  timeSeriesCitedSeries,
  trendRef,
  suppressedValueKeys,
  isStreaming,
  onPreviewDocument,
  onPreviewTrend,
  ResponseText,
}: {
  text: string
  citations: ResponseSegment['citations']
  citationLookup: Map<string, DocumentTableRow>
  timeSeriesSummaries?: Map<string, TimeSeriesSeriesSummary>
  timeSeriesCitedSeries?: Map<string, TimeSeriesComputedSeries>
  trendRef?: TimeSeriesTrendRef
  suppressedValueKeys?: ReadonlySet<string>
  isStreaming: boolean
  onPreviewDocument?: PreviewDocumentHandler
  onPreviewTrend?: PreviewTrendHandler
  ResponseText: React.ComponentType<{ children: React.ReactNode }>
}): React.ReactElement {
  const [citationHovered, setCitationHovered] = useState(false)

  return (
    <Box display='flex' direction='column' gap='xs' minWidth='none'>
      <Box
        surface={citationHovered ? 'muted' : 'transparent'}
        radius='md'
        padding='xs'
        minWidth='none'>
        <ResponseText>
          <ResponseBodyText isStreaming={isStreaming} text={text} />
        </ResponseText>
      </Box>
      <ResponseSegmentCitations
        citations={citations}
        citationLookup={citationLookup}
        timeSeriesSummaries={timeSeriesSummaries}
        timeSeriesCitedSeries={timeSeriesCitedSeries}
        trendRef={trendRef}
        suppressedValueKeys={suppressedValueKeys}
        onCitationHoverChange={setCitationHovered}
        onPreviewDocument={onPreviewDocument}
        onPreviewTrend={onPreviewTrend}
      />
    </Box>
  )
}

export function useCitationLookup(): Map<string, DocumentTableRow> {
  const { documentResults, citationDocumentLookup } = useDocumentSearchData()
  return useMemo(() => {
    if (citationDocumentLookup.size > 0) {
      return citationDocumentLookup
    }
    return buildCitationDocumentLookup(documentResults)
  }, [citationDocumentLookup, documentResults])
}

export function CitedResponseSegments({
  event,
  onPreviewDocument,
  onPreviewTrend,
  ResponseText,
  wrapItems,
}: {
  event: DocChatResponseEvent | DocChatResponsePartialEvent
  onPreviewDocument?: PreviewDocumentHandler
  onPreviewTrend?: PreviewTrendHandler
  ResponseText: React.ComponentType<{ children: React.ReactNode }>
  wrapItems?: (items: React.ReactElement[]) => React.ReactNode
}): React.ReactElement | null {
  const citationLookup = useCitationLookup()

  const timeSeriesSummaries = useMemo(() => {
    const map = new Map<string, TimeSeriesSeriesSummary>()
    if ('time_series' in event && event.time_series) {
      event.time_series.series.forEach((summary) => {
        map.set(summary.entry_id, summary)
      })
    }
    return map
  }, [event])

  const trendRef: TimeSeriesTrendRef | undefined =
    'time_series' in event && event.time_series?.trend
      ? event.time_series.trend
      : undefined

  const timeSeriesCitedSeries = useMemo(() => {
    const map = new Map<string, TimeSeriesComputedSeries>()
    if (!('time_series' in event) || !event.time_series) return map
    const plotted = (event.time_series.chart_commands ?? []).flatMap(
      (command) => command.computed_series ?? []
    )
    for (const series of [
      ...plotted,
      ...(event.time_series.cited_series ?? []),
    ]) {
      map.set(series.entry_id, series)
    }
    return map
  }, [event])

  const isStreaming = event.type === 'response_partial'

  // The follow-up questions are not part of the answer: the turn shows them
  // under its newest answer only (DocChatTurnSuggestions).
  const segments = useMemo(
    () =>
      (event.response_segments ?? []).filter(
        (segment) => segment.kind !== 'suggestions'
      ),
    [event.response_segments]
  )

  // A details block shows no citations, so its citations must not hide the
  // same value's chip on the next paragraph. Its place stays, for the indexes.
  const suppressedValueKeysBySegment = useMemo(
    () =>
      adjacentDuplicateValueCitationKeys(
        segments.map((segment) =>
          segment.kind === 'details' ? { ...segment, citations: [] } : segment
        )
      ),
    [segments]
  )

  if (segments.length) {
    const items = segments.map((segment, segmentIndex) => {
      if (segment.kind === 'details') {
        return (
          <ResponseDetailsSegment
            key={segmentIndex}
            text={segment.text}
            title={segment.title}
            isStreaming={isStreaming}
          />
        )
      }
      return (
        <CitedResponseSegment
          key={segmentIndex}
          text={segment.text}
          citations={segment.citations}
          citationLookup={citationLookup}
          isStreaming={isStreaming}
          timeSeriesSummaries={timeSeriesSummaries}
          timeSeriesCitedSeries={timeSeriesCitedSeries}
          trendRef={trendRef}
          suppressedValueKeys={suppressedValueKeysBySegment[segmentIndex]}
          onPreviewDocument={onPreviewDocument}
          onPreviewTrend={onPreviewTrend}
          ResponseText={ResponseText}
        />
      )
    })
    if (wrapItems) return <>{wrapItems(items)}</>
    return (
      <Box display='flex' direction='column' gap='md' minWidth='none'>
        {items}
      </Box>
    )
  }

  const responseText = getDocChatResponseBodyText(event)
  if (!responseText) return null

  const fallback = (
    <ResponseText key='body'>
      <ResponseBodyText isStreaming={isStreaming} text={responseText} />
    </ResponseText>
  )
  if (wrapItems) return <>{wrapItems([fallback])}</>
  return fallback
}
